var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { getFontAwesomeIcon, hasValue, isFontawesomeIcon } from '@utils';
import { getInputSizeStyles, getInputStateClass } from './utils';
import { UXPContext } from "@core/context/Context";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import { Dropdown } from '../../popups/Dropdown';
import classNames from 'classnames';
import DataList from '../../list/DataList';
import SearchBox from './SearchBox';
import useToast from "@notifications/Toast/useToast";
import useDebounce from "@hooks/useDebounce";
import ButtonComponent from "@widget/buttons/ButtonComponent";
import DropdownIndicator from "@widget/other/DropdownIndicator";
const SelectBase = (props) => {
    const context = React.useContext(UXPContext);
    // props
    let { selected, options, labelField = 'label', valueField = 'value', placeholder, className, isValid, onChange, showEndOfContent = false, iconField, renderOption, addNewValues, dropdownClassname, renderCustomDropdownContent, renderPlaceholder, dropdownMinHeight, dropdownMinWidth, dropdownMaxWidth, onClear, pageSize, selectedLabel, minWidth, maxWidth, width, fullWidth, disabled, readOnly, prefix, position } = props;
    // states
    const [showPicker, setShowPicker] = React.useState(false);
    const [selectedOption, setSelectedOption] = React.useState(null);
    const [query, setQuery] = React.useState("");
    const [selIndex, setSelIndex] = React.useState(-1);
    const [filteredOptions, setFilteredOptions] = React.useState([]);
    const [savingNewValue, setSavingNewValue] = React.useState(false);
    const [loadingSelectedOption, setLoadingSelectedOption] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const debouncedSearchQuery = useDebounce(query);
    const optionContainerRef = React.useRef(null);
    const selectRef = React.useRef(null);
    const toast = useToast();
    const hasIcon = (!!iconField && (iconField === null || iconField === void 0 ? void 0 : iconField.trim().length) > 0);
    if (!hasValue(placeholder))
        placeholder = context.$L('uxp-core.text.select-an-item');
    const isDynamicLoading = typeof options == 'function';
    React.useEffect(() => {
        if (Array.isArray(options)) {
            setFilteredOptions(options);
        }
    }, [options]);
    // Reset filtered options when dropdown opens
    React.useEffect(() => {
        if (showPicker && !isDynamicLoading) {
            setFilteredOptions(Array.isArray(options) ? options : []);
            setQuery("");
        }
    }, [showPicker, options, isDynamicLoading]);
    React.useEffect(() => {
        updateSelectedOption();
    }, [selected, filteredOptions, selectedLabel]);
    // scroll the highlighted option into view on keyboard navigation
    React.useEffect(() => {
        if (selIndex < 0)
            return;
        let options = getOptions();
        let selOpt = options[selIndex];
        if (selOpt) {
            selOpt.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [selIndex]);
    // filter out option dom nodes as html elements
    const getOptions = () => {
        let container = optionContainerRef.current;
        let options = [];
        if (container) {
            options = Array.from(container === null || container === void 0 ? void 0 : container.querySelectorAll(".uxpcore_select__option"));
        }
        return options;
    };
    function updateSelectedOption() {
        return __awaiter(this, void 0, void 0, function* () {
            setLoadingSelectedOption(true);
            let selOpt = { [labelField]: "", [valueField]: "", [iconField]: "" };
            if (placeholder)
                selOpt = { [labelField]: placeholder, [valueField]: "", [iconField]: "" };
            if (selected) {
                const foundOption = filteredOptions.find((option) => option[valueField] === selected);
                if (foundOption) {
                    selOpt = foundOption;
                }
                else if (!!selectedLabel) {
                    if (typeof selectedLabel == 'string') {
                        selOpt = { [labelField]: selectedLabel, [valueField]: selected };
                    }
                    if (typeof selectedLabel == 'function') {
                        try {
                            const opt = yield selectedLabel(selected);
                            if (!!opt)
                                selOpt = opt;
                        }
                        catch (e) {
                            console.error('Unable to get the selected option details. error:', e);
                        }
                    }
                }
            }
            setSelectedOption(selOpt);
            setLoadingSelectedOption(false);
        });
    }
    // handle backdrop ref
    const closePicker = () => {
        setQuery("");
        setSelIndex(-1);
        setShowPicker(false);
        setTimeout(() => { var _a; return (_a = selectRef === null || selectRef === void 0 ? void 0 : selectRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    //handle select 
    const handleSelect = (option) => {
        onChange(option[valueField], option);
        closePicker();
    };
    // handle input change
    const handleInputChange = (val) => {
        setQuery(val);
        setSelIndex(-1);
        if (!isDynamicLoading) {
            let regEx = new RegExp(val, 'i');
            let _options = options.filter((opt) => { var _a, _b; return regEx.test(((_b = (_a = opt[labelField]) === null || _a === void 0 ? void 0 : _a.toLowerCase) === null || _b === void 0 ? void 0 : _b.call(_a)) || ''); });
            setFilteredOptions(_options);
        }
    };
    // handle keyboard inputs
    const handleKeyboardInputs = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            const len = isDynamicLoading ? getOptions().length : filteredOptions.length;
            if (len === 0)
                return;
            setSelIndex(prev => (prev + 1 < len ? prev + 1 : 0));
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            const len = isDynamicLoading ? getOptions().length : filteredOptions.length;
            if (len === 0)
                return;
            setSelIndex(prev => (prev <= 0 ? len - 1 : prev - 1));
        }
        // discard on esc
        if (e.key == 'Escape') {
            e.stopPropagation();
            closePicker();
            setQuery("");
        }
        // change on enter
        if (e.key == 'Enter') {
            if (selIndex >= 0) {
                if (!isDynamicLoading) {
                    // data-driven: directly use the filtered options array
                    const item = filteredOptions[selIndex];
                    if (item) {
                        onChange(item[valueField], item);
                        closePicker();
                    }
                }
                else {
                    // dynamic loading: fall back to DOM data-value attribute
                    const domOptions = getOptions();
                    const selOpt = domOptions[selIndex];
                    if (selOpt) {
                        const item = JSON.parse(selOpt.dataset.value);
                        onChange(item[valueField], item);
                        closePicker();
                    }
                }
            }
        }
    };
    function getIcon(opt) {
        if (!opt)
            return null;
        if (!opt[iconField] || opt[iconField].trim().length == 0)
            return null;
        let icon = null;
        if (isFontawesomeIcon(opt[iconField])) {
            const parts = getFontAwesomeIcon(opt[iconField]);
            icon = _jsx("div", { className: "fa-icon", children: _jsx(FontAwesomeIconWrapper, { icon: parts }) });
        }
        else {
            icon = _jsx("div", { className: "image-icon", children: _jsx("div", { className: "icon", style: {
                        backgroundImage: `url(${opt[iconField]})`
                    } }) });
        }
        return icon;
    }
    function addNewValue() {
        var _a;
        /* Scenario B: host opens a form / dialog / etc.; we don't enter
         * the savingNewValue async flow and we don't auto-select. */
        if (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.onClick) {
            addNewValues.onClick();
            closePicker();
            return;
        }
        /* Scenario A: existing async create-from-typed-text flow. */
        if (!hasValue(query)) {
            toast.error('Value is required');
            return;
        }
        setSavingNewValue(true);
        (_a = addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.onAddNewValue) === null || _a === void 0 ? void 0 : _a.call(addNewValues, query).then(res => {
            onChange(query, res);
            closePicker();
        }).catch(e => { }).finally(() => {
            setSavingNewValue(false);
        });
    }
    let AddNewValuesElement = null;
    if (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) {
        AddNewValuesElement = _jsx("div", { className: 'uxpcore_dropdown-action-row', onMouseDown: (e) => {
                /* Row owns the click so the whole strip is hit-testable.
                 * Inner button is purely visual; pointer-events disabled
                 * in SCSS to avoid double-fire. */
                e.preventDefault();
                if (!savingNewValue)
                    addNewValue();
            }, children: _jsx(ButtonComponent, { title: savingNewValue ? (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.loadingTitle) || '' : (addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.title) || '', onClick: () => { }, disabled: savingNewValue, size: 'smaller', variant: 'link' }) });
    }
    const showAddNewAlways = !!(addNewValues === null || addNewValues === void 0 ? void 0 : addNewValues.enable) && !!addNewValues.alwaysShow;
    const pillStyles = {};
    if (!!(renderPlaceholder === null || renderPlaceholder === void 0 ? void 0 : renderPlaceholder.renderAsPill)) {
        pillStyles.width = 'auto';
        pillStyles.display = 'inline-block';
        const { minWidth, maxWidth } = renderPlaceholder.renderAsPill;
        if (hasValue(minWidth))
            pillStyles.minWidth = minWidth + 'px';
        if (hasValue(maxWidth))
            pillStyles.maxWidth = maxWidth + 'px';
    }
    const loadPaginatedData = React.useCallback((max, lastToken, args) => __awaiter(void 0, void 0, void 0, function* () {
        return options(max, lastToken, Object.assign(Object.assign({}, args), { query: debouncedSearchQuery }));
    }), [options, debouncedSearchQuery]);
    const memorizedArgs = React.useMemo(() => {
        return hasValue(query) ? { query } : {};
    }, [query]);
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const renderDropdownContent = () => (_jsx("div", { onKeyDown: !renderCustomDropdownContent ? handleKeyboardInputs : undefined, children: !!renderCustomDropdownContent
            ? _jsx(_Fragment, { children: renderCustomDropdownContent(() => { setShowPicker(false); }) })
            : _jsxs(_Fragment, { children: [_jsx(SearchBox, { value: query, onChange: handleInputChange, placeholder: selectedOption && selectedOption[labelField] ? selectedOption[labelField] : "", autoFocus: true, className: "uxpcore_select__search" }), _jsx("div", { className: "uxpcore_select__dropdown-list", ref: optionContainerRef, children: _jsx(DataList, { data: isDynamicLoading ? loadPaginatedData : filteredOptions, pageSize: pageSize || 6, renderItem: (opt, key) => {
                                let icon = getIcon(opt);
                                return _jsx("div", { className: classNames("uxpcore_select__option", {
                                        "uxpcore_select__option--selected": selectedOption[valueField] === opt[valueField],
                                        "uxpcore_select__option--highlighted": key == selIndex
                                    }), onClick: () => handleSelect(opt), onMouseOver: () => setSelIndex(key), "data-value": JSON.stringify(opt), children: renderOption
                                        ? renderOption(opt, key, key == selIndex, (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption[valueField]) === opt[valueField])
                                        : _jsxs("div", { className: classNames("uxpcore_select__option-content", { 'uxpcore_select__option-content--has-icon': hasIcon && !!icon }), children: [hasIcon && _jsxs("div", { className: "uxpcore_select__option-icon", children: [" ", icon, " "] }), _jsx("div", { className: "uxpcore_select__option-label", children: opt[labelField] })] }) }, key);
                            }, showFooter: false, showEndOfContent: showEndOfContent, renderNoItems: () => {
                                return AddNewValuesElement;
                            }, args: memorizedArgs }) }), showAddNewAlways && AddNewValuesElement] }) }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    /**
     * Handle trigger click to toggle dropdown
     *
     * Click isolation is guaranteed:
     * - This trigger is in normal DOM (this component)
     * - Dropdown renders to document.body via portal
     * - Clicking trigger toggles state here
     * - Clicking outside (backdrop) calls onToggle via Dropdown
     * - No double-firing: trigger and backdrop are in separate DOM trees
     */
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    // Handle focus to show focus styles
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    // Handle blur to remove focus styles
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    // Dropdown indicator icon - chevron up/down
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    // Clear button — shown whenever a value is selected (not disabled/readOnly)
    // onClear overrides the default behaviour (which calls onChange(''))
    const clearButton = (hasValue(selected) && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear ? onClear() : onChange('', undefined);
        }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    // Calculate state classes
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    // External trigger pattern (v5):
    // - Custom trigger matching Input structure (prefix, value, suffix)
    // - Uses .uxpcore_input base class for consistent styling
    // - Dropdown is sibling (external trigger pattern)
    return (_jsxs("div", { className: classNames("uxpcore_select", className), style: Object.assign(Object.assign({}, pillStyles), containerStyles), children: [!!(renderPlaceholder === null || renderPlaceholder === void 0 ? void 0 : renderPlaceholder.renderCustomPill) ? (_jsx("div", { ref: selectRef, onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, className: "uxpcore_select__custom-trigger", children: renderPlaceholder.renderCustomPill(onClear) })) : (
            /* Standard trigger - matches Input.tsx structure */
            _jsxs("div", { ref: selectRef, className: classNames("uxpcore_input", "uxpcore_input--select", stateClass, {
                    "uxpcore_input--has-prefix": !!prefix,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": hasValue(selected),
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [prefix && (_jsx("div", { className: "uxpcore_input__prefix", children: prefix })), _jsx("div", { className: "uxpcore_input__value", children: loadingSelectedOption ? (_jsx(FontAwesomeIconWrapper, { icon: ['fas', 'spinner'], spin: true })) : ((selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption[labelField]) || placeholder || '') }), (clearButton || dropdownIndicator) && (_jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] }))] })), _jsx(Dropdown, { externalTriggerRef: selectRef, content: renderDropdownContent(), position: hasValue(position) ? position : (context === null || context === void 0 ? void 0 : context.direction) == "rtl" ? "bottom-right" : "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_select__dropdown", dropdownClassname), matchTriggerWidth: true, minWidth: dropdownMinWidth, maxWidth: dropdownMaxWidth || (hasValue(dropdownMinWidth) ? dropdownMinWidth : "20rem") })] }));
};
/**
 *
 * A select control to select one item from a list of multiple items
 * @export
 *
 *  @example
 * ```
 *  // options
 *  let [selectedOption, setSelectedOption] = React.useState<string>(null)
 *  let options = [
 *      {label: "Sri Lanka", value: "SL"},
 *      {label: "India", value: "IN"},
 *      {label: "United State", value: "US"},
 *  ]
 *
 *  <Select
 *      options={options}
 *      selected={selectedOption}
 *      onChange={(newValue, option) => {
 *          setSelectedOption(value)
 *      }}
 *  />
 * ```
 *
 * @example
 * ```
 *  // options
 *  let [selectedOption, setSelectedOption] = React.useState<string>(null)
 *  let options = [
 *      {name: "Sri Lanka", code: "SL"},
 *      {name: "India", code: "IN"},
 *      {name: "United State", code: "US"},
 *  ]
 *
 *  <Select
 *      options={options}
 *      labelField="name"
 *      valueField="code"
 *      selected={selectedOption}
 *      onChange={(newValue, option) => {
 *          setSelectedOption(value)
 *      }}
 *  />
 * ```
 *
 */
const Select = React.memo((props) => {
    return _jsx(SelectBase, Object.assign({}, props));
});
export default React.memo(Select);
